import type { ComponentType } from 'react'
import type { LucideProps } from 'lucide-react-native'
import type { ReactNode } from 'react'
import { ActivityIndicator, Image, Pressable, Text, View } from 'react-native'
import { twMerge } from 'tailwind-merge'
import { colors } from '@chillout/core'

interface ButtonProps {
  label: string
  onPress: () => void
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost'
  icon?: ReactNode
  accessibilityLabel?: string
  accessibilityHint?: string
  className?: string
  fullWidth?: boolean
  disabled?: boolean
  loading?: boolean
  loadingLabel?: string
  LeftIcon?: ComponentType<LucideProps>
  LeftImage?: { source: ReturnType<typeof require>; width?: number; height?: number }
}

export function Button({
  label,
  onPress,
  variant = 'primary',
  icon,
  accessibilityLabel,
  accessibilityHint,
  className,
  fullWidth = true,
  disabled = false,
  loading = false,
  loadingLabel,
  LeftIcon,
  LeftImage,
}: ButtonProps) {
  const isDisabled = disabled || loading
  const visibleLabel = loading ? (loadingLabel ?? label) : label
  const isPrimary = variant === 'primary'
  const isSecondary = variant === 'secondary'
  const isOutline = variant === 'outline'
  const isGhost = variant === 'ghost'
  const iconColor = isPrimary ? colors.text.inverse
    : (isOutline || isGhost) ? colors.accent.DEFAULT
    : isDisabled ? colors.text.muted
    : colors.text.primary
  const disabledTextClassName = isPrimary ? 'text-inverse' : (isOutline || isGhost) ? 'text-accent' : 'text-muted'
  const enabledTextClassName = isPrimary ? 'text-inverse' : (isOutline || isGhost) ? 'text-accent' : 'text-primary'


  return (
    <Pressable
      onPress={onPress}
      disabled={isDisabled}
      accessibilityLabel={accessibilityLabel ?? visibleLabel}
      accessibilityHint={accessibilityHint}
      accessibilityRole="button"
      accessibilityState={{ disabled: isDisabled, busy: loading }}
      className={twMerge(
        isPrimary ? 'border border-transparent bg-accent' : '',
        isSecondary ? 'border border-border bg-transparent' : '',
        isOutline ? 'border border-accent bg-transparent' : '',
        fullWidth ? 'w-full' : 'self-center',
        'items-center rounded-lg px-6 py-4 web:lg:py-2.5',
        isDisabled ? 'opacity-50' : '',
        className,
      )}
    >
      <View className="flex-row items-center justify-center gap-2">
        {loading ? (
          <ActivityIndicator
            size="small"
            color={isPrimary ? colors.text.inverse : colors.accent.DEFAULT}
          />
        ) : LeftIcon ? (
          <LeftIcon size={20} color={iconColor} strokeWidth={1.5} accessible={false} />
        ) : LeftImage ? (
          <Image
            source={LeftImage.source}
            style={{ width: LeftImage.width ?? 20, height: LeftImage.height ?? 20 }}
            resizeMode="contain"
            accessible={false}
          />
        ) : icon ?? null}
        <Text
          className={`text-base lg:text-lg font-semibold ${isDisabled ? disabledTextClassName : enabledTextClassName}`}
        >
          {visibleLabel}
        </Text>
      </View>
    </Pressable>
  )
}
